@if (isDetailsLoaded == null) {
  <div class="body-padding">
    <div class="w-full flex flex-col items-center justify-center gap-4 py-20">
      <loading-spinner [size]="'4rem'"/>
      <div class="text-xl text-gray-600 dark:text-gray-400">Loading quote details...</div>
    </div>
  </div>
} @else if (!isDetailsLoaded) {
  <div id="quote-not-found" class="body-padding">
    <div class="w-full text-xl flex items-center justify-center">
      Quote Not Found
    </div>
  </div>
} @else {
  <div id="quote-details-page" class="body-padding">
    <div class="flex flex-col items-baseline mb-4 gap-4 w-full">
      <span>
        <div id="quote-breadcrumb">
          <span id="quote-back-link" (click)="returnToQuotes()"
                class="text-blue-500 text-sm font-normal cursor-pointer">Quotes</span>
          <span id="quote-id-breadcrumb" class="text-zinc-500 text-sm font-normal"> / #{{ quoteId }}</span>
        </div>
      </span>
      <div class="flex gap-3 justify-between">
        <div id="quote-title" class="text-gray-900 text-[32px] font-bold font-['Roboto Slab'] leading-9 tracking-wide">
          Quote #{{ quoteId }}
        </div>
        <div>
          <button id="quote-export-pdf-btn" [disabled]="isPdfExportRunning" (click)="openPdf()"
                  class="text-sm bg-yellow-300 disabled:bg-gray-200 p-2 rounded-lg">
            @if (!isPdfExportRunning) {
              <div id="quote-export-pdf-text" class="flex">
                Export as PDF
              </div>
            }
            @if (isPdfExportRunning) {
              <div id="quote-export-pdf-loading" class="flex items-center justify-center gap-3">
                Please wait while we are generating PDF document...
                <div role="status">
                  <svg aria-hidden="true"
                       class="w-6 h-6 mr-2 text-gray-100 animate-spin dark:text-gray-500 fill-red-600"
                       viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"/>
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"/>
                  </svg>
                  <span class="sr-only">Loading...</span>
                </div>
              </div>
            }
          </button>
        </div>
      </div>

      @if (quoteDetails?.children_quote_ids?.length > 0 || quoteDetails?.parent_quote_id?.length > 0) {
        <div id="quote-related-quotes" class="self-stretch justify-start items-start gap-4 inline-flex">
          <div class="w-[56px] text-zinc-500 text-base font-normal font-['Inter'] leading-loose">Quote(s):</div>
          @if (quoteDetails?.children_quote_ids?.length > 0) {
            <div id="quote-children-dropdown" class="relative h-8 rounded-2xl justify-center items-center gap-2.5 flex">
              <div id="quote-children-dropdown-toggle"
                   class="text-center text-blue-500 text-base font-semibold font-['Inter'] pl-2 pr-2 leading-loose cursor-pointer flex items-center"
                   (click)="toggleDropdown($event)">
                <span class="inline-block mr-2 text-black text-2xl transform transition-transform duration-300"
                      [ngClass]="{'rotate-90': isDropdownOpen, '-rotate-90': !isDropdownOpen}">
                  ‹
                </span>
                <a href="javascript:void(0);" class="no-underline text-black">
                  {{ quoteDetails.children_quote_ids[0].child_quote_id }}
                </a>
              </div>
              <!-- Dropdown Content for Children Quotes -->
              @if (isDropdownOpen) {
                <div id="quote-children-dropdown-content"
                     class="absolute top-10 left-0 bg-white border border-gray-200 rounded-md shadow-md w-[150px] max-h-48 overflow-y-auto z-10">
                  <ul class="flex flex-col">
                    @for (item of quoteDetails?.children_quote_ids; track item.child_quote_id; let i = $index) {
                      <li
                        id="quote-children-item-{{i}}"
                        class="px-4 py-2 hover:bg-gray-100 text-black-500 cursor-pointer"
                        (mouseenter)="showJobNumber(item.child_quote_id)"
                        (mouseleave)="hideJobNumber()">
                        <a id="quote-children-link-{{i}}" href="javascript:void(0);"
                           (click)="goToQuote(item.child_quote_id)" class="no-underline text-black-500">
                          {{ item.child_quote_id }}
                        </a>
                        <!-- Display job_number only on hover -->
                        @if (hoveredQuoteId === item.child_quote_id && item.job_number) {
                          <span id="quote-children-job-{{i}}"
                                class="block text-sm text-gray-600 mt-1 cursor-pointer"
                                (click)="goToJob(item.job_id)"> Job Number: {{ item.job_number }}
                          </span>
                        }
                      </li>
                    }
                  </ul>
                </div>
              }
            </div>
          }
          @if (quoteDetails?.parent_quote_id?.length > 0) {
            <div id="quote-parent-dropdown" class="relative h-8 rounded-2xl justify-center items-center gap-2.5 flex">
              <div id="quote-parent-dropdown-toggle"
                   class="text-center text-blue-500 text-base font-semibold font-['Inter'] pl-2 pr-2 leading-loose cursor-pointer flex items-center"
                   (click)="toggleDropdown($event)">
                <span class="inline-block mr-2 text-black text-2xl transform transition-transform duration-300"
                      [ngClass]="{'rotate-90': isDropdownOpen, '-rotate-90': !isDropdownOpen}">
                  ‹
                </span>
                <a href="javascript:void(0);" class="no-underline text-black">
                  {{ quoteDetails?.parent_quote_details?.parent_quote_id }}
                </a>
              </div>
              <!-- Dropdown Content for Parent Quote -->
              @if (isDropdownOpen) {
                <div id="quote-parent-dropdown-content"
                     class="absolute top-10 left-0 bg-white border border-gray-200 rounded-md shadow-md w-[150px] max-h-48 overflow-y-auto z-10">
                  <ul class="flex flex-col">
                    <li id="quote-parent-item" class="px-4 py-2 hover:bg-gray-100 text-black-500 cursor-pointer"
                        (mouseenter)="showJobNumber(quoteDetails?.parent_quote_details?.parent_quote_id)"
                        (mouseleave)="hideJobNumber()">
                      <a id="quote-parent-link" href="javascript:void(0);"
                         (click)="goToQuote(quoteDetails?.parent_quote_details?.parent_quote_id)"
                         class="no-underline text-black-500">
                        {{ quoteDetails?.parent_quote_details?.parent_quote_id }}
                      </a>
                      <!-- Display job_number only on hover -->
                      @if (hoveredQuoteId === quoteDetails?.parent_quote_details?.parent_quote_id && quoteDetails?.parent_quote_details?.parent_job_number) {
                        <span id="quote-parent-job"
                              class="block text-sm text-gray-600 mt-1 cursor-pointer"
                              (click)="goToJob(quoteDetails?.parent_quote_details?.parent_job_id)"> Job Number: {{ quoteDetails?.parent_quote_details?.parent_job_number }}
                        </span>
                      }
                    </li>
                  </ul>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>

    <div class="flex flex-wrap items-baseline align-baseline">
      <div id="quote-info-card" class="w-full px-8 py-4 bg-white rounded-xl shadow">
        <key-value-pair-table [data]="quoteDetailsFields"/>

        @if (awaitsCustomerAction(quoteDetails?.quote_status_id)) {
          <div id="quote-approve-container" class="mt-4">
            <quote-approve-popup [status]="quoteDetails?.quote_status_id"
                                 [deselectedItems]="deselectedItems"
                                 [quoteLineItems]="quoteAssetsLineItems"/>
          </div>
        }
      </div>
    </div>

    <div id="quote-tabs-container" class="w-full">
      <ul
        class="flex gap-2 flex-wrap text-lg font-normal text-center border-b border-gray-400 dark:border-gray-600 mb-1"
        id="default-tab" role="tablist">
        <li>
          <button id="assets-tab" class="tab-button" [class.selected-tab-button]="activeTab === 'assets'"
                  type="button" role="tab" aria-controls="assets" (click)="changeTab('assets')">
            Assets ({{ quoteAssets.length }})
          </button>
        </li>
        <li>
          <button id="upcoming-tab" class="tab-button" [class.selected-tab-button]="activeTab === 'attachments'"
                  type="button" role="tab" aria-controls="attachments" (click)="changeTab('attachments')">
            Attachments ({{ getTotalNumber(quoteAttachments) }})
          </button>
        </li>
        <li>
          <button id="upcoming-tab" class="tab-button" [class.selected-tab-button]="activeTab === 'comments'"
                  type="button" role="tab" aria-controls="comments" (click)="changeTab('comments')">
            Comments ({{ quoteComments.length }})
          </button>
        </li>
        <li>
          <button id="upcoming-tab" class="tab-button" [class.selected-tab-button]="activeTab === 'history'"
                  type="button" role="tab" aria-controls="history" (click)="changeTab('history')">
            History ({{ historyTableData.data.length }})
          </button>
        </li>
      </ul>
    </div>
    <div id="quote-tabs-content">
      @switch (activeTab) {
        @case ('assets') {
          @if (combinedAssetsDetails.length > 0) {
            <table id="quote-assets-table" class="min-w-full">
              <thead>
              <tr class="text-left text-s text-gray-500 tracking-wider">
                <th scope="col" class="px-6 py-3 font-medium">Asset</th>
                <th scope="col" class="pr-6 py-3 font-medium"></th>
                <th scope="col" class="px-6 py-3 font-medium">Asset #</th>
                <th scope="col" class="px-6 py-3 font-medium">PO Number</th>
                <th scope="col" class="px-6 py-3 font-medium">Issue Description</th>
              </tr>
              </thead>
              <tbody class="bg-white">
                @for (assetDetail of combinedAssetsDetails; track $index; let i = $index) {
                  <tr id="quote-asset-row-{{i}}">
                    <td class="w-0.5 text-black pl-6">{{ i + 1 }}</td>
                    <td class="pr-6 py-4 whitespace-nowrap text-sm text-black ml-4">
                      <div class="flex">
                        <strong id="quote-asset-name-{{i}}" [ngStyle]="{ color: assetDetail.is_owned ? null : 'gray' }">
                          {{ assetDetail.asset || '--' }}
                        </strong>
                        @if (!assetDetail.is_owned) {
                          <div id="quote-asset-warning-{{i}}" class="custom-tooltip-not-owned-warning">
                            <svg class="w-4 h-4 text-gray-500" aria-hidden="true"
                                 xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                 viewBox="0 0 24 20">
                              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M12 13V8m0 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                            </svg>
                            <div class="custom-tooltip-not-owned-warning-text">
                              This Asset has been relocated to another Company.
                            </div>
                          </div>
                        }
                      </div>
                      <div id="quote-asset-serial-{{i}}" class="text-xs text-gray-500 mt-1">
                        SN:<strong> {{ assetDetail.serial_number || '--' }}</strong></div>
                    </td>
                    <td id="quote-asset-number-{{i}}"
                        class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ assetDetail.asset_number || "--" }}
                    </td>
                    <td id="quote-asset-po-{{i}}"
                        class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ assetDetail.po_number || "--" }}
                    </td>
                    <td id="quote-asset-issue-{{i}}" class="px-6 py-4 whitespace-nowrap text-sm text-black">
                      <pre>{{ assetDetail.customer_concern || "--" }}</pre>
                    </td>
                  </tr>
                  <tr id="quote-asset-checks-row-{{i}}">
                    <td colspan="5" class="px-6 py-2 text-sm">
                      <div class="text-gray-500 mb-2">Device Checks</div>
                      <div id="quote-asset-checks-{{i}}"
                           class="text-black whitespace-pre-wrap">{{ assetDetail.device_checks || '--' }}
                      </div>
                    </td>
                  </tr>
                  <tr id="quote-asset-actions-row-{{i}}">
                    <td colspan="5" class="px-6 py-2 text-sm">
                      <div class="text-gray-500">Corrective Actions</div>
                      <div id="quote-asset-actions-{{i}}"
                           class="text-black whitespace-pre-wrap">{{ assetDetail.corective_actions || '--' }}
                      </div>
                    </td>
                  </tr>
                  <td id="quote-asset-pricing-row-{{i}}" colspan="5" class="px-6 py-4">
                    <div class="border-b-2 pb-4">
                      <div class="text-sm font-normal font-['Inter'] text-zinc-500 mb-2">Corrective Pricing</div>
                      @if (assetDetail.correctivePricing.length > 0) {
                        <div>
                          <div id="quote-asset-pricing-table-container-{{i}}"
                               class="overflow-hidden rounded-lg border-2 border-gray-200">
                            <table id="quote-asset-pricing-table-{{i}}" class="min-w-full">
                              <thead class="bg-transparent">
                              <tr class="border-b-2 text-left text-xs font-semibold tracking-wider text-[#84868C]">
                                <th class="px-6 py-3">Line Item</th>
                                <th class="px-6 py-3">Part Number</th>
                                <th class="px-6 py-3">UOM</th>
                                <th class="px-6 py-3">QTY</th>
                                <th class="px-6 py-3">Rate</th>
                                <th class="px-6 py-3">Price</th>
                                <th class="px-6 py-3 text-center">Mandatory</th>
                              </tr>
                              </thead>
                              <tbody>
                                @for (item of assetDetail.correctivePricing; track $index; let j = $index) {
                                  <tr id="quote-asset-pricing-item-{{i}}-{{j}}" class="border-b-2">
                                    <td id="quote-asset-pricing-lineitem-{{i}}-{{j}}"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-black">
                                      <div class="flex items-center gap-3">
                                        @if (lineItemImageUrl(item); as url) {
                                          <img id="quote-asset-pricing-image-{{i}}-{{j}}" [src]="url"
                                               [alt]="item.line_item || 'Line item'"
                                               class="w-10 h-10 shrink-0 rounded border border-gray-200 object-contain bg-white"
                                               (error)="onLineItemImageError(url)"/>
                                        } @else {
                                          <span class="w-10 h-10 shrink-0 rounded border border-dashed border-gray-300 bg-gray-50"
                                                title="No image available" aria-hidden="true"></span>
                                        }
                                        <span>{{ item.line_item || "--" }}</span>
                                      </div>
                                    </td>
                                    <td id="quote-asset-pricing-partnumber-{{i}}-{{j}}"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ item.qb_part_number || "--" }}
                                    </td>
                                    <td id="quote-asset-pricing-uom-{{i}}-{{j}}"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ item.unit_of_measure_full_name || "--" }}
                                    </td>
                                    <td id="quote-asset-pricing-qty-{{i}}-{{j}}"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ item.quantity || "--" }}
                                    </td>
                                    <td id="quote-asset-pricing-rate-{{i}}-{{j}}"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ formatCurrency(item.rate) || "--" }}
                                    </td>
                                    <td id="quote-asset-pricing-price-{{i}}-{{j}}"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-black">{{ formatCurrency(item.price) || "--" }}
                                    </td>
                                    <td id="quote-asset-pricing-mandatory-{{i}}-{{j}}"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-black text-center">
                                      @if (item.is_mandatory) {
                                        <div class="relative group inline-block">
                                          <input type="checkbox"
                                                 [checked]="true"
                                                 [disabled]="true"
                                                 class="w-4 h-4 bg-gray-100 checked:bg-blue-600 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"/>
                                          <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                            You cannot deselect mandatory item
                                          </div>
                                        </div>
                                      } @else {
                                        @if (awaitsCustomerAction(quoteDetails?.quote_status_id)) {
                                          <input type="checkbox"
                                                 [checked]="getSelectedValue(item.quote_line_item_id)"
                                                 (change)="onSelectionChange(item.quote_line_item_id, $event)"
                                                 class="w-4 h-4 bg-gray-100 checked:bg-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"/>
                                        } @else {
                                          <input type="checkbox"
                                                 [checked]="item.is_mandatory"
                                                 [disabled]="true"
                                                 class="w-4 h-4 bg-gray-100 checked:bg-blue-600 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"/>
                                        }
                                      }
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                            <div id="quote-asset-pricing-summary-{{i}}" class="flex justify-end my-2 pr-2">
                              <div class="text-right text-sm font-semibold text-[#84868C]">
                                <div id="quote-asset-pricing-subtotal-{{i}}" class="pt-2">Asset Subtotal: <span
                                  class="text-black">{{ calculateAssetSubtotal(assetDetail.correctivePricing) | currency }}</span>
                                </div>
                                <div id="quote-asset-pricing-tax-{{i}}" class="pt-2">Tax: <span
                                  class="text-black">{{ calculateTotalTax(assetDetail.correctivePricing) | currency }}</span>
                                </div>
                                <div id="quote-asset-pricing-total-{{i}}" class="pt-2">Asset Total: <span
                                  class="text-black">{{ calculateTotalWithTax(assetDetail.correctivePricing) | currency }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      } @else {
                        <div id="quote-assets-no-data" class="w-full text-start">--</div>
                      }
                    </div>
                  </td>
                }
              </tbody>
            </table>
          } @else {
            <div id="quote-assets-no-data" class="w-full text-start">--</div>
          }
          <div class="flex gap-4 text-sm bg-white px-6 py-2">
            <div class="flex-1">
              @if (quoteDetails?.show_p21_tax_warning) {
                <div class="flex flex-1 items-center bg-[#fbf7cd] rounded p-2">
                  <div class="flex gap-2">
                    <svg class="flex-shrink-0 w-[24px] h-[24px] text-[#c9722c]" viewBox="0 0 24 24" fill="none"
                         xmlns="http://www.w3.org/2000/svg"
                         role="presentation">
                      <path fill-rule="evenodd" clip-rule="evenodd"
                            d="M13.4897 4.34592L21.8561 18.8611C21.9525 19.0288 22.0021 19.2181 21.9999 19.4101C21.9977 19.6021 21.9438 19.7903 21.8435 19.9559C21.7432 20.1215 21.6001 20.2588 21.4282 20.3542C21.2563 20.4497 21.0616 20.4999 20.8636 20.5H3.13707C2.93882 20.5 2.74401 20.4498 2.57196 20.3543C2.39992 20.2588 2.25663 20.1213 2.15631 19.9556C2.05598 19.7898 2.00212 19.6015 2.00006 19.4093C1.998 19.2171 2.04782 19.0278 2.14456 18.86L10.5121 4.34592C10.6602 4.08939 10.8762 3.87577 11.1377 3.72708C11.3993 3.57838 11.6971 3.5 12.0003 3.5C12.3036 3.5 12.6013 3.57838 12.8629 3.72708C13.1245 3.87577 13.3404 4.08939 13.4885 4.34592H13.4897ZM12.0003 7.82538C11.8232 7.82537 11.6482 7.86212 11.4869 7.93317C11.3257 8.00423 11.182 8.10793 11.0656 8.2373C10.9492 8.36668 10.8627 8.51872 10.8119 8.68321C10.7611 8.8477 10.7473 9.02083 10.7713 9.19093L11.3546 13.3416C11.3754 13.4933 11.4523 13.6326 11.5711 13.7334C11.6899 13.8343 11.8424 13.8899 12.0003 13.8899C12.1582 13.8899 12.3107 13.8343 12.4295 13.7334C12.5483 13.6326 12.6253 13.4933 12.6461 13.3416L13.2293 9.19093C13.2533 9.02083 13.2395 8.8477 13.1887 8.68321C13.138 8.51872 13.0515 8.36668 12.935 8.2373C12.8186 8.10793 12.6749 8.00423 12.5137 7.93317C12.3525 7.86212 12.1774 7.82537 12.0003 7.82538V7.82538ZM12.0003 17.3369C12.3395 17.3369 12.6649 17.2062 12.9047 16.9737C13.1446 16.7412 13.2793 16.4258 13.2793 16.0969C13.2793 15.7681 13.1446 15.4527 12.9047 15.2202C12.6649 14.9877 12.3395 14.857 12.0003 14.857C11.6611 14.857 11.3358 14.9877 11.0959 15.2202C10.8561 15.4527 10.7213 15.7681 10.7213 16.0969C10.7213 16.4258 10.8561 16.7412 11.0959 16.9737C11.3358 17.2062 11.6611 17.3369 12.0003 17.3369V17.3369Z"
                            fill="currentColor"></path>
                    </svg>
                    <p>Please note that all quotes are subject to applicable state and local sales taxes, which will be
                      calculated at the time of order placement. If your organization is exempt from sales tax, please
                      provide a
                      valid sales tax exemption certificate to ensure the exemption is applied to your order.</p>
                  </div>
                </div>
              }
            </div>
            <div class="flex flex-col gap-2 text-sm font-semibold text-[#84868C] text-right">
              <div>Sub-Total: <span class="text-black">{{ calculateTotalSubtotalForAllAssets() | currency }}</span></div>
              <div>Tax: <span class="text-black">{{ calculateTotalTaxForAllAssets() | currency }}</span></div>
              <div>Total: <span class="text-black">{{ calculateTotalWithTaxForAllAssets() | currency }}</span></div>
            </div>
          </div>
        }
        @case ('attachments') {
          <quote-attachments id="quote-attachments-component" [attachments]="quoteAttachments"/>
        }
        @case ('comments') {
          <comments-section id="quote-comments-component" [data]="quoteComments" [loading]="loadingComments"
                            (create)="createComment($event)" (edit)="editComment($event)"
                            (delete)="deleteComment($event)"/>
        }
        @case ('history') {
          <history-table id="quote-history-component" [data]="historyTableData" [loading]="loadingHistory"/>
        }
      }
    </div>
  </div>
}

<ng-template #boldField let-data>
  <span class="font-bold">{{ data }}</span>
</ng-template>
<ng-template #tagField let-data>
  <tags [tags]="[data]"/>
</ng-template>
<ng-template #locationField let-data>
  <div class="flex flex-col">
    <span class="font-semibold">{{ data.name }}</span>
    <span>{{ data.address }}</span>
  </div>
</ng-template>
<ng-template #descriptionField let-data>
  <span class="font-bold leading-loose">{{ data }}</span>
</ng-template>
